/**
 * IndexedDB service for backing up recordings before upload
 * This provides a safety net in case uploads fail or browser crashes
 */

const DB_NAME = 'RecordingBackupDB';
const DB_VERSION = 1;
const STORE_NAME = 'recordings';

/**
 * Open IndexedDB connection
 * @returns {Promise<IDBDatabase>}
 */
const openDB = () => {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result);

    request.onupgradeneeded = (event) => {
      const db = event.target.result;

      // Create object store if it doesn't exist
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        const objectStore = db.createObjectStore(STORE_NAME, { keyPath: 'id', autoIncrement: true });

        // Create indexes for querying
        objectStore.createIndex('assignment_id', 'assignment_id', { unique: false });
        objectStore.createIndex('question_id', 'question_id', { unique: false });
        objectStore.createIndex('timestamp', 'timestamp', { unique: false });
        objectStore.createIndex('uploaded', 'uploaded', { unique: false });
      }
    };
  });
};

/**
 * Save recording blobs to IndexedDB
 * @param {Object} recordingData - Recording data to backup
 * @param {number} recordingData.assignment_id - Assignment ID
 * @param {number} recordingData.question_id - Question ID
 * @param {Blob} recordingData.screenBlob - Screen recording blob
 * @param {Blob} recordingData.webcamBlob - Webcam recording blob (optional)
 * @param {Blob} recordingData.audioBlob - Audio recording blob (optional)
 * @param {string} recordingData.comments - User comments (optional)
 * @returns {Promise<number>} - ID of the saved record
 */
export const saveRecordingBackup = async (recordingData) => {
  try {
    const db = await openDB();

    const record = {
      assignment_id: recordingData.assignment_id,
      question_id: recordingData.question_id,
      screenBlob: recordingData.screenBlob,
      webcamBlob: recordingData.webcamBlob,
      audioBlob: recordingData.audioBlob,
      comments: recordingData.comments,
      timestamp: new Date().toISOString(),
      uploaded: false
    };

    return new Promise((resolve, reject) => {
      const transaction = db.transaction([STORE_NAME], 'readwrite');
      const objectStore = transaction.objectStore(STORE_NAME);
      const request = objectStore.add(record);

      request.onsuccess = () => {
        console.log('Recording backed up to IndexedDB with ID:', request.result);
        resolve(request.result);
      };
      request.onerror = () => reject(request.error);

      transaction.oncomplete = () => db.close();
    });
  } catch (error) {
    console.error('Error saving recording backup:', error);
    throw error;
  }
};

/**
 * Mark a recording as successfully uploaded
 * @param {number} id - ID of the backup record
 * @returns {Promise<void>}
 */
export const markRecordingAsUploaded = async (id) => {
  try {
    const db = await openDB();

    return new Promise((resolve, reject) => {
      const transaction = db.transaction([STORE_NAME], 'readwrite');
      const objectStore = transaction.objectStore(STORE_NAME);
      const getRequest = objectStore.get(id);

      getRequest.onsuccess = () => {
        const record = getRequest.result;
        if (record) {
          record.uploaded = true;
          record.uploadedAt = new Date().toISOString();
          const updateRequest = objectStore.put(record);

          updateRequest.onsuccess = () => resolve();
          updateRequest.onerror = () => reject(updateRequest.error);
        } else {
          resolve(); // Record doesn't exist, nothing to update
        }
      };
      getRequest.onerror = () => reject(getRequest.error);

      transaction.oncomplete = () => db.close();
    });
  } catch (error) {
    console.error('Error marking recording as uploaded:', error);
    throw error;
  }
};

/**
 * Get all recordings that haven't been uploaded yet
 * @returns {Promise<Array>} - Array of pending recording backups
 */
export const getPendingRecordings = async () => {
  try {
    const db = await openDB();

    return new Promise((resolve, reject) => {
      const transaction = db.transaction([STORE_NAME], 'readonly');
      const objectStore = transaction.objectStore(STORE_NAME);
      const index = objectStore.index('uploaded');
      const request = index.getAll(false);

      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);

      transaction.oncomplete = () => db.close();
    });
  } catch (error) {
    console.error('Error getting pending recordings:', error);
    return [];
  }
};

/**
 * Delete a recording backup
 * @param {number} id - ID of the backup record to delete
 * @returns {Promise<void>}
 */
export const deleteRecordingBackup = async (id) => {
  try {
    const db = await openDB();

    return new Promise((resolve, reject) => {
      const transaction = db.transaction([STORE_NAME], 'readwrite');
      const objectStore = transaction.objectStore(STORE_NAME);
      const request = objectStore.delete(id);

      request.onsuccess = () => {
        console.log('Recording backup deleted from IndexedDB:', id);
        resolve();
      };
      request.onerror = () => reject(request.error);

      transaction.oncomplete = () => db.close();
    });
  } catch (error) {
    console.error('Error deleting recording backup:', error);
    throw error;
  }
};

/**
 * Delete old uploaded recordings (cleanup - keep only last 24 hours)
 * @returns {Promise<number>} - Number of records deleted
 */
export const cleanupOldBackups = async () => {
  try {
    const db = await openDB();
    const twentyFourHoursAgo = new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString();

    return new Promise((resolve, reject) => {
      const transaction = db.transaction([STORE_NAME], 'readwrite');
      const objectStore = transaction.objectStore(STORE_NAME);
      const request = objectStore.getAll();

      request.onsuccess = () => {
        const records = request.result;
        let deletedCount = 0;

        records.forEach(record => {
          // Delete if uploaded and older than 24 hours
          if (record.uploaded && record.uploadedAt && record.uploadedAt < twentyFourHoursAgo) {
            objectStore.delete(record.id);
            deletedCount++;
          }
        });

        console.log(`Cleaned up ${deletedCount} old recording backups`);
        resolve(deletedCount);
      };
      request.onerror = () => reject(request.error);

      transaction.oncomplete = () => db.close();
    });
  } catch (error) {
    console.error('Error cleaning up old backups:', error);
    return 0;
  }
};

/**
 * Get total size of all stored recordings
 * @returns {Promise<number>} - Total size in bytes
 */
export const getBackupStorageSize = async () => {
  try {
    const db = await openDB();

    return new Promise((resolve, reject) => {
      const transaction = db.transaction([STORE_NAME], 'readonly');
      const objectStore = transaction.objectStore(STORE_NAME);
      const request = objectStore.getAll();

      request.onsuccess = () => {
        const records = request.result;
        let totalSize = 0;

        records.forEach(record => {
          if (record.screenBlob) totalSize += record.screenBlob.size;
          if (record.webcamBlob) totalSize += record.webcamBlob.size;
          if (record.audioBlob) totalSize += record.audioBlob.size;
        });

        resolve(totalSize);
      };
      request.onerror = () => reject(request.error);

      transaction.oncomplete = () => db.close();
    });
  } catch (error) {
    console.error('Error getting backup storage size:', error);
    return 0;
  }
};
